import React from 'react';
import {
  MessageSquare, Building2, BarChart2, Calendar, FileText,
  Database, Bell, Settings, Columns3, Users,
} from 'lucide-react';
import { HQPanelId } from '../lib/types';
import { HQ_DOCK_APPS } from '../lib/config';

const ICONS: Record<string, React.ReactNode> = {
  MessageSquare: <MessageSquare size={18} />,
  Building2: <Building2 size={18} />,
  Columns3: <Columns3 size={18} />,
  BarChart2: <BarChart2 size={18} />,
  Calendar: <Calendar size={18} />,
  FileText: <FileText size={18} />,
  Database: <Database size={18} />,
  Bell: <Bell size={18} />,
  Settings: <Settings size={18} />,
  Users: <Users size={18} />,
};

interface DockProps {
  activePanel: HQPanelId;
  onSelect: (panel: HQPanelId) => void;
  notificationCount?: number;
}

/**
 * Dock — macOS-style dock for KAELO HQ.
 * Floating pill centered at bottom. On mobile, icons shrink and the pill becomes
 * horizontally scrollable so it still feels like a macOS dock rather than a flat
 * nav bar.
 */
export default function Dock({ activePanel, onSelect, notificationCount = 0 }: DockProps) {
  return (
    <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 z-50 select-none max-w-[calc(100vw-16px)]">
      <div
        className="flex items-center gap-0.5 md:gap-1 px-2 md:px-3 py-1.5 md:py-2 rounded-2xl border border-gray-200/80 shadow-sm overflow-x-auto scrollbar-hide"
        style={{ backgroundColor: 'rgba(255,255,255,0.92)' }}
        data-testid="hq-dock"
      >
        {HQ_DOCK_APPS.map((app) => {
          const isActive = app.id === activePanel;
          const hasBadge = app.id === 'notifications' && notificationCount > 0;
          return (
            <button
              key={app.id}
              onClick={() => onSelect(app.id)}
              className="dock-icon relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl transition-colors hover:bg-gray-100 shrink-0"
              style={isActive ? { backgroundColor: '#f3f4f6' } : {}}
              title={app.label}
              data-testid={`dock-item-${app.id}`}
            >
              <span className={isActive ? 'text-gray-800' : 'text-gray-500'}>
                {ICONS[app.icon]}
              </span>
              {hasBadge && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-badge">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
              {isActive && <div className="absolute -bottom-1 w-1 h-1 rounded-full bg-gray-700" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
